import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  collection,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
  updateDoc,
  doc,
} from "@firebase/firestore";
import { SnackMessages } from "../../../components";
import { auth, db } from "../../../firebase";
import {
  actionGetCustomerSalesUSers,
  actionGetCustomersByEmails,
} from "../../../actions/Dashboard/UserManagement";
import { Card, Col, Placeholder, Row } from "react-bootstrap";
import ChatBody from "./ChatBody";
import ChatHead from "./ChatHead";
import "./index.scss";
import moment from "moment";
import { Avatars } from "../../../components/Avatars";

const Chat = () => {
  const [chat, setChat] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [userId, setUserId] = useState();
  const [customer, setCustomer] = useState(null);
  const [realChat, setRealChat] = useState(null);
  const [initialChat, setInitialChat] = useState(null);
  const [isChatLoading, setChatLoading] = useState(false);
  const [isChatBodyLoading, setChatBodyLoading] = useState(true);
  const [chatBodyVisible, setChatBodyVisible] = useState(true);
  const [activeChatId, setActiveChatId] = useState(null);
  let dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const emailParam = searchParams.get("email");

  const customerRef = useRef(customer);
  const chatRef = useRef(chat);
  const unsubscribeRef = useRef(null);

  useEffect(() => {
    customerRef.current = customer;
  }, [customer]);

  useEffect(() => {
    chatRef.current = chat;
  }, [chat]);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  const playNotificationSound = () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const playChime = (time, frequency) => {
        const osc = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(frequency, time);
        gainNode.gain.setValueAtTime(0.35, time);
        gainNode.gain.exponentialRampToValueAtTime(0.01, time + 0.35);
        osc.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        osc.start(time);
        osc.stop(time + 0.35);
      };
      const now = audioCtx.currentTime;
      playChime(now, 587.33); // D5 chime
      playChime(now + 0.12, 880); // A5 chime
    } catch (error) {
      console.error("Failed to play notification sound:", error);
    }
  };

  const showDesktopNotification = (senderName, messageText, docData) => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
      const orderId = docData?.lastOrderId;
      const notificationTitle = orderId
        ? `New message from (Order #${orderId})`
        : `New message from ${senderName}`;
      const notification = new Notification(notificationTitle, {
        body: messageText,
        icon: "/favicon.ico",
      });
      notification.onclick = () => {
        window.focus();
        if (docData && docData.email) {
          setSearchParams({ email: docData.email });
        }
      };
    }
  };

  const getTimestampSeconds = (t) => {
    if (!t) return 0;
    if (typeof t.seconds === "number") return t.seconds;
    if (typeof t.toMillis === "function") return t.toMillis() / 1000;
    if (t instanceof Date) return t.getTime() / 1000;
    if (typeof t === "number") return t / 1000;
    return 0;
  };

  useEffect(() => {
    if (!activeChatId) return;

    const userDocRef = doc(db, "users", activeChatId);
    updateDoc(userDocRef, {
      isAdminInChat: true,
    }).catch((err) => console.error("Error setting admin presence true", err));

    const handleBeforeUnload = () => {
      updateDoc(userDocRef, {
        isAdminInChat: false,
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      updateDoc(userDocRef, {
        isAdminInChat: false,
      }).catch((err) =>
        console.error("Error setting admin presence false", err),
      );
    };
  }, [activeChatId]);

  const getUserChat = async (e) => {
    if (!e || !e.email) {
      console.warn("getUserChat: No customer or email provided!");
      return;
    }
    try {
      const userResponse = query(
        collection(db, "users"),
        where("email", "==", e.email.toLowerCase().trim()),
      );
      const userData = await getDocs(userResponse);
      let id = "";
      userData.forEach((_data) => {
        id = _data.id;
      });
      setCustomer(e);
      setActiveChatId(id);

      if (!id) {
        setChatHistory([]);
        return;
      }

      const response = query(
        collection(db, "users", id, "messages"),
        orderBy("createdOn", "asc"),
      );
      const data = await getDocs(response);
      const dataUsers = [];
      data.forEach((_data) => {
        dataUsers.push(_data.data());
      });
      setChatHistory(dataUsers);
    } catch (err) {
      console.error("getUserChat: Error fetching user chat:", err);
    }
  };

  const fetchPost = async () => {
    setChatLoading(true);
    try {
      const rawUserData = localStorage.getItem("userData");
      const getUserEmail = JSON.parse(rawUserData);

      const users = query(
        collection(db, "users"),
        where(
          "email",
          "==",
          getUserEmail?.email ? getUserEmail.email.toLowerCase().trim() : "",
        ),
      );
      const allUsers = await getDocs(users);
      let userId = "";
      allUsers.forEach((doc) => {
        userId = doc.id;
      });
      setUserId(userId);

      const response = query(collection(db, "users"));
      const data = await getDocs(response);
      const dataUsers = [];
      data.forEach((_data) => {
        dataUsers.push(_data.data());
      });

      const q = query(collection(db, "users"));
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      unsubscribeRef.current = onSnapshot(q, (querySnapshot) => {
        const currentChatList = chatRef.current || [];
        const currentCustomer = customerRef.current;
        let listChanged = false;
        let newChatList = [...currentChatList];

        querySnapshot.docChanges().forEach((change) => {
          if (change.type === "modified" || change.type === "added") {
            const docData = change.doc.data();
            const userEmail = docData.email;

            // Find the user in our current chat list
            const existingUserIdx = newChatList.findIndex(
              (u) =>
                u.email &&
                userEmail &&
                u.email.toLowerCase().trim() === userEmail.toLowerCase().trim(),
            );

            if (existingUserIdx !== -1) {
              const existingUser = newChatList[existingUserIdx];

              // Check if there is a new message from this user
              const isNewMessage =
                docData.lastMessageTime &&
                (!existingUser.lastMessageTime ||
                  getTimestampSeconds(docData.lastMessageTime) >
                    getTimestampSeconds(existingUser.lastMessageTime));

              const isCurrentUser =
                currentCustomer &&
                userEmail &&
                currentCustomer.email &&
                currentCustomer.email.toLowerCase().trim() ===
                  userEmail.toLowerCase().trim();

              // If it's a new message, and it's NOT from the active customer OR the browser tab is hidden/unfocused, and the message is unread
              if (
                isNewMessage &&
                (!isCurrentUser || document.hidden || !document.hasFocus()) &&
                docData.isSeen === false
              ) {
                playNotificationSound();
                showDesktopNotification(
                  docData.name || userEmail,
                  docData.lastMessage,
                  docData,
                );
              }

              const isAdminViewingThis =
                isCurrentUser && !document.hidden && document.hasFocus();

              // Update the user details in our list
              newChatList[existingUserIdx] = {
                ...existingUser,
                lastMessage: docData.lastMessage,
                lastMessageTime: docData.lastMessageTime,
                isSeen: isAdminViewingThis
                  ? true
                  : (docData.isSeen ?? existingUser.isSeen),
                unreadCount: isAdminViewingThis
                  ? 0
                  : (docData.unreadCount ?? existingUser.unreadCount),
                isAdminInChat:
                  docData.isAdminInChat ?? existingUser.isAdminInChat,
                isMobileInChat:
                  docData.isMobileInChat ?? existingUser.isMobileInChat,
              };
              listChanged = true;
            } else {
              // If the user is not in the list but has messages, add them
              if (docData.lastMessageTime !== undefined) {
                newChatList.push(docData);
                listChanged = true;
              }
            }

            // If the changed document is the current active customer, reload their chat history
            if (
              currentCustomer &&
              userEmail &&
              currentCustomer.email &&
              userEmail.toLowerCase().trim() ===
                currentCustomer.email.toLowerCase().trim()
            ) {
              getUserChat(currentCustomer);
              if (!document.hidden && document.hasFocus()) {
                handleMessageRead(currentCustomer);
              }
            }
          }
        });

        if (listChanged) {
          // Sort the list by lastMessageTime descending
          newChatList.sort((a, b) => {
            const timeA = a.lastMessageTime ? a.lastMessageTime.toDate() : 0;
            const timeB = b.lastMessageTime ? b.lastMessageTime.toDate() : 0;
            return timeB - timeA;
          });
          setChat(newChatList);
          setInitialChat(newChatList);
        }
      });

      const res = await dispatch(
        actionGetCustomersByEmails(dataUsers.map((e) => e.email)),
      );

      const updateList = dataUsers
        .map((objB) => {
          const matchingObjA = res?.data?.find(
            (objA) => objA.email === objB.email,
          );
          if (matchingObjA) {
            return { ...objB, image: matchingObjA.profile_image };
          } else {
            return objB;
          }
        })
        .filter((e) => e?.lastMessageTime !== undefined)
        .sort(
          (a, b) =>
            moment(b?.lastMessageTime?.toDate()) -
            moment(a?.lastMessageTime?.toDate()),
        );

      const initialEmail = new URLSearchParams(window.location.search).get(
        "email",
      );
      let initialSelectedChat = updateList[0];
      if (initialEmail) {
        const found = updateList.find(
          (c) =>
            c.email &&
            initialEmail &&
            c.email.toLowerCase().trim() === initialEmail.toLowerCase().trim(),
        );
        if (found) {
          initialSelectedChat = found;
        } else {
          const foundInAll = dataUsers.find(
            (c) =>
              c.email &&
              initialEmail &&
              c.email.toLowerCase().trim() ===
                initialEmail.toLowerCase().trim(),
          );
          if (foundInAll) {
            initialSelectedChat = foundInAll;
          }
        }
      }
      setChat(updateList);
      handleChat(initialSelectedChat);
      setChatLoading(false);
      setInitialChat(updateList);
    } catch (err) {
      console.error("fetchPost error:", err);
      setChatLoading(false);
    }
  };

  const handleChat = (e) => {
    setChatBodyVisible(true);
    getChatUsers(e?.email);
    getUserChat(e);
    setRealChat(e);
    handleMessageRead(e);
    if (e?.email && searchParams.get("email") !== e.email) {
      setSearchParams({ email: e.email }, { replace: true });
    }
  };

  useEffect(() => {
    if (emailParam && chat.length > 0) {
      const found = chat.find(
        (c) =>
          c.email &&
          emailParam &&
          c.email.toLowerCase().trim() === emailParam.toLowerCase().trim(),
      );
      if (found) {
        if (
          !customer ||
          !customer.email ||
          customer.email.toLowerCase().trim() !==
            found.email.toLowerCase().trim()
        ) {
          handleChat(found);
        }
      }
    }
  }, [emailParam, chat, customer]);

  const handleMessageRead = async (e) => {
    if (!e || !e.email) return;
    const q = query(
      collection(db, "users"),
      where("email", "==", e.email.toLowerCase().trim()),
    );
    const querySnapShot = await getDocs(q);
    const queryData = querySnapShot.docs.map((e) => ({
      id: e.id,
    }));

    // const res = await doc(db, `users/${e.id}`).set({ capital: true })
    queryData.map(async (e) => {
      await updateDoc(doc(db, `users/${e.id}`), {
        isSeen: true,
        unreadCount: 0,
      });
      setChatBodyLoading(false);
    });
  };
  const CustomLoader = () => (
    // <div className='d-flex justify-content-center align-items-center h-100'>
    //     <Avatars src="/logo.gif" variant={"gif-loader"} />
    // </div>
    <Card className={"h-100 p-3 gap-4"}>
      <Row>
        <Col lg={2}>
          <Placeholder className="avatar-loader h-100" animation="glow">
            <Placeholder xs={12} />
          </Placeholder>
        </Col>
        <Col lg={10}>
          <div className="d-flex flex-column">
            <Placeholder className="avatar-loader" animation="glow">
              <Placeholder xs={12} />
            </Placeholder>
            <Placeholder className="avatar-loader" animation="glow">
              <Placeholder xs={8} />
            </Placeholder>
          </div>
        </Col>
      </Row>
      <Row>
        <Col lg={2}>
          <Placeholder className="avatar-loader h-100" animation="glow">
            <Placeholder xs={12} />
          </Placeholder>
        </Col>
        <Col lg={10}>
          <div className="d-flex flex-column">
            <Placeholder className="avatar-loader" animation="glow">
              <Placeholder xs={12} />
            </Placeholder>
            <Placeholder className="avatar-loader" animation="glow">
              <Placeholder xs={7} />
            </Placeholder>
          </div>
        </Col>
      </Row>
      <Row>
        <Col lg={2}>
          <Placeholder className="avatar-loader h-100" animation="glow">
            <Placeholder xs={12} />
          </Placeholder>
        </Col>
        <Col lg={10}>
          <div className="d-flex flex-column">
            <Placeholder className="avatar-loader" animation="glow">
              <Placeholder xs={11} />
            </Placeholder>
            <Placeholder className="avatar-loader" animation="glow">
              <Placeholder xs={7} />
            </Placeholder>
          </div>
        </Col>
      </Row>
      <Row>
        <Col lg={2}>
          <Placeholder className="avatar-loader h-100" animation="glow">
            <Placeholder xs={12} />
          </Placeholder>
        </Col>
        <Col lg={10}>
          <div className="d-flex flex-column">
            <Placeholder className="avatar-loader" animation="glow">
              <Placeholder xs={7} />
            </Placeholder>
            <Placeholder className="avatar-loader" animation="glow">
              <Placeholder xs={11} />
            </Placeholder>
          </div>
        </Col>
      </Row>
      <Row>
        <Col lg={2}>
          <Placeholder className="avatar-loader h-100" animation="glow">
            <Placeholder xs={12} />
          </Placeholder>
        </Col>
        <Col lg={10}>
          <div className="d-flex flex-column">
            <Placeholder className="avatar-loader" animation="glow">
              <Placeholder xs={9} />
            </Placeholder>
            <Placeholder className="avatar-loader" animation="glow">
              <Placeholder xs={6} />
            </Placeholder>
          </div>
        </Col>
      </Row>
    </Card>
  );
  const CustomLoaderBody = () =>
    chatBodyVisible && (
      <Card
        className={"h-100 p-3 gap-4 d-flex flex-column justify-content-center"}
      >
        <Placeholder
          className="avatar-loader d-flex justify-content-center"
          animation="glow"
        >
          <Placeholder xs={5} />
        </Placeholder>
        <Placeholder
          className="avatar-loader d-flex justify-content-center"
          animation="glow"
        >
          <Placeholder xs={8} />
        </Placeholder>
        <Placeholder
          className="avatar-loader d-flex justify-content-center"
          animation="glow"
        >
          <Placeholder xs={6} />
        </Placeholder>
        <Placeholder
          className="avatar-loader d-flex justify-content-center"
          animation="glow"
        >
          <Placeholder xs={7} />
        </Placeholder>
        <Placeholder
          className="avatar-loader d-flex justify-content-center"
          animation="glow"
        >
          <Placeholder xs={3} />
        </Placeholder>
      </Card>
    );

  const handleSearch = (e) => {
    let filteredChat = initialChat;
    if (e.target.value) {
      filteredChat = [...chat].filter((item) => {
        return item.name.toLowerCase().includes(e.target.value.toLowerCase());
      });
    }
    setChat(filteredChat);
  };

  const getChatUsers = React.useCallback(async (email) => {
    const res = await dispatch(actionGetCustomerSalesUSers(email));
    if (!res.success) {
      const { showErrorMessage } = SnackMessages();
      // showErrorMessage(res.message)
    }
  }, []);

  useEffect(() => {
    fetchPost();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setChatBodyLoading(false);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.title = "Tab Surfaces Admin - Chats";
  }, []);

  return (
    <div className="chat flex-nowrap flex-fill-row">
      <div className="h-100">
        <Row className="h-100 position-relative">
          <Col lg={5} xxl={4} className={"chat-trigger h-100"}>
            {isChatLoading ? (
              <CustomLoader />
            ) : (
              <ChatHead
                chatUsers={chat}
                handleChat={handleChat}
                customer={customer}
                handleSearch={handleSearch}
              />
            )}
          </Col>
          {chatBodyVisible && (
            <Col lg={7} xxl={8} className={"chat-container h-100"}>
              {isChatBodyLoading ? (
                <CustomLoaderBody />
              ) : (
                <ChatBody
                  chatHistory={chatHistory}
                  userId={userId}
                  customer={customer}
                  getUserChat={getUserChat}
                  fetchPost={fetchPost}
                  handleChatBody={() => {
                    setChatBodyVisible(false);
                    setActiveChatId(null);
                  }}
                  chatUsers={chat}
                />
              )}
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
};

export default React.memo(Chat);
